/**
 * ★★ I18N-02 — LES TEXTES DE LA VITRINE, EN FRANÇAIS, ALLEMAND ET ANGLAIS.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI UN FICHIER SÉPARÉ, ICI ET PAS AILLEURS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La règle générale du produit est d'écrire le dictionnaire **au point
 * d'usage** : trois lignes dans le JSX, et une phrase supprimée emporte ses
 * trois traductions avec elle. C'est ce que fait `useTraduire()` partout.
 *
 * La vitrine est l'exception, pour une raison de volume : vingt-quatre
 * sections, plusieurs centaines de phrases, souvent longues. Les garder dans
 * le JSX y noierait la structure de la page sous le texte, et c'est justement
 * la page dont la mise en forme demande le plus de relecture visuelle.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ CE QUE LE FRANÇAIS EST, ET CE QUE LES DEUX AUTRES NE SONT PAS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * **Le français fait foi.** Il a été négocié phrase par phrase avec le client
 * — les arbitrages du 22/08 sont écrits dans les en-têtes des sections, et
 * `vitrine.test.tsx` en verrouille quatre-vingt-neuf mot pour mot. Une
 * traduction ne le modifie JAMAIS : si une tournure allemande demande de
 * changer le français, c'est le français qu'on rouvre, avec le client.
 *
 * ⚠️⚠️ **L'ALLEMAND ET L'ANGLAIS N'ONT PAS ÉTÉ RELUS PAR UN NATIF.** Ils sont
 *      utilisables pour éprouver le mécanisme, la mise en page et les
 *      débordements ; ils ne sont **pas** prêts pour un prospect alémanique.
 *      Une page de vente se juge à la justesse d'un registre, pas à
 *      l'exactitude d'un sens — et c'est précisément ce qu'une traduction non
 *      relue ne peut pas garantir. À faire relire avant toute mise en
 *      production de la version allemande.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TROIS PARTIS PRIS DE TRADUCTION
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  1. **L'allemand est du Hochdeutsch de Suisse** : `ss` et jamais `ß`, qui ne
 *     s'écrit pas en Suisse. Une page qui vend à Berne avec des `ß` se signale
 *     comme allemande d'Allemagne dès la première ligne.
 *  2. **Les noms propres ne se traduisent pas** — ILYGO, et le nom du produit.
 *  3. **Les montants ne sont pas dans ce fichier.** Ils vivent dans
 *     `SectionTarifs` et n'ont pas de langue : CHF 3'400 s'écrit pareil en
 *     allemand. Les traduire serait la meilleure façon d'en faire diverger un.
 */

import type { Traduit } from '@tb/shared'

/* ========================================================================== */
/* L'EN-TÊTE                                                                  */
/* ========================================================================== */

export const ENTETE = {
  /**
   * Le nom du produit dans la barre.
   *
   * ★ « Billetterie & abonnements » est une DESCRIPTION, pas une marque : elle
   *   se traduit. « ILYGO » est la marque et reste tel quel — d'où la
   *   séparation des deux en deux chaînes plutôt qu'une seule.
   */
  produit: {
    fr: 'Billetterie & abonnements',
    de: 'Ticketing & Abonnements',
    en: 'Ticketing & season passes',
  },
  parIlygo: { fr: '· par ILYGO', de: '· von ILYGO', en: '· by ILYGO' },
  navigation: { fr: 'Vitrine', de: 'Übersicht', en: 'Overview' },
  ceQueCaFait: { fr: 'Ce que ça fait', de: 'Funktionen', en: 'What it does' },
  laCampagne: { fr: 'La campagne', de: 'Die Kampagne', en: 'The campaign' },
  tarif: { fr: 'Tarif', de: 'Preis', en: 'Pricing' },
  documentation: { fr: 'Documentation', de: 'Dokumentation', en: 'Documentation' },
  nousEcrire: { fr: 'Nous écrire', de: 'Kontakt', en: 'Contact us' },
  essai: {
    fr: 'Essayer trente jours',
    de: 'Dreissig Tage testen',
    en: 'Try it for thirty days',
  },
} as const satisfies Record<string, Traduit>

/* ========================================================================== */
/* LA BARRE D'ACTION MOBILE                                                   */
/* ========================================================================== */

export const BARRE_ACTION = {
  essai: ENTETE.essai,
  leTarif: { fr: 'Le tarif', de: 'Der Preis', en: 'Pricing' },
} as const satisfies Record<string, Traduit>

/* ========================================================================== */
/* LE PIED DE PAGE                                                            */
/* ========================================================================== */

export const PIED = {
  /**
   * ★ L'aiguillage du pied : le visiteur qui cherche SA billetterie et non le
   *   logiciel. C'est la phrase la plus utile de la page pour quelqu'un qui
   *   s'est trompé d'adresse, et elle doit rester limpide dans les trois
   *   langues.
   */
  chercheSaBilletterieTitre: {
    fr: 'Vous cherchez la billetterie de votre théâtre ?',
    de: 'Suchen Sie das Ticketing Ihres Theaters?',
    en: 'Looking for your theatre’s ticket office?',
  },
  chercheSaBilletterieTexte: {
    fr: 'Elle a sa propre adresse : celle que votre salle communique à son public. Vous y trouverez sa boutique, son espace abonné et la connexion de son équipe.',
    de: 'Es hat seine eigene Adresse: diejenige, die Ihr Haus seinem Publikum mitteilt. Dort finden Sie den Ticketshop, den Abonnentenbereich und den Zugang für das Team.',
    en: 'It has its own address — the one your venue gives its audience. There you will find its shop, its subscriber area and its team login.',
  },
  /**
   * ★★ LA MENTION DE TVA, UNE FOIS ET UNE SEULE (arbitrage du 22/08).
   *    Elle couvre TOUS les montants de la page. Ne jamais accoler « HT » à un
   *    prix ailleurs — la règle vaut dans les trois langues.
   */
  tva: {
    fr: 'Tous les montants de cette page s’entendent hors taxes. La TVA suisse de 8,1 % s’ajoute à la facture.',
    de: 'Alle Beträge auf dieser Seite verstehen sich exklusive Steuern. Die Schweizer Mehrwertsteuer von 8,1 % kommt auf der Rechnung hinzu.',
    en: 'All amounts on this page are exclusive of tax. Swiss VAT of 8.1 % is added on the invoice.',
  },
  /**
   * ⚠️ La raison sociale est injectée à l'exécution (`RAISON_SOCIALE_ILYGO`) :
   *    le texte porte donc `{editeur}`, jamais le nom en dur. Un nom d'éditeur
   *    recopié dans trois traductions est un nom qu'on oubliera de changer
   *    deux fois sur trois.
   */
  editeurLigne: {
    fr: '{editeur} : billetterie et abonnements de saison pour les salles de Suisse romande.',
    de: '{editeur}: Ticketing und Saisonabonnements für Spielstätten in der Westschweiz.',
    en: '{editeur}: ticketing and season subscriptions for venues in French-speaking Switzerland.',
  },
  liensPied: { fr: 'Liens de pied de page', de: 'Fusszeilen-Links', en: 'Footer links' },
} as const satisfies Record<string, Traduit>

/* -------------------------------------------------------------------------- */
/* Interpolation                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Remplace `{clé}` par une valeur, dans une phrase déjà traduite.
 *
 * ★ Volontairement minuscule et sans pluralisation. Les rares phrases de la
 *   vitrine qui portent un nombre l'écrivent en toutes lettres dans chaque
 *   langue : une phrase dont le pluriel dépend d'une variable est une phrase
 *   qu'il vaut mieux réécrire que mécaniser, et l'allemand comme l'anglais
 *   n'ont pas les mêmes règles d'accord que le français.
 */
export function interpoler(phrase: string, valeurs: Readonly<Record<string, string>>): string {
  return phrase.replaceAll(/\{(\w+)\}/gu, (entier, cle: string) => valeurs[cle] ?? entier)
}
